import type { Id } from "@convex/_generated/dataModel";
/**
 * Shared Category Confirmation Dialog
 *
 * Shown when editing a category that's shared across multiple menus.
 * Allows user to either update all shared instances or save as a new category.
 */
import type { UseMutationResult } from "@tanstack/react-query";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { MultiCombobox } from "~/components/ui/multi-combobox";

export interface SharedCategoryDialogProps {
  updateSharedCategoryStatus: "confirm" | "select-menus" | null;
  setUpdateSharedCategoryStatus: (
    status: "confirm" | "select-menus" | null
  ) => void;
  category: {
    name: string;
    itemCount?: number | null;
    menuRelations: Array<{
      [key: string]: unknown;
      menu: { [key: string]: unknown; id: string; name: string };
    }>;
  };
  uniqueLocations: Array<{
    id: string;
    name: string;
    logoUrl: string | null;
  }>;
  menusToCopyCategoryTo: string[];
  setMenusToCopyCategoryTo: (menus: string[]) => void;
  updateCategoryMutation: UseMutationResult<
    unknown,
    Error,
    {
      id: Id<"categories">;
      name?: string;
      color?: string;
      matchBusinessHours?: boolean;
      isCategoryLockEnabled?: boolean;
    }
  >;
  cloneAndUpdateCategoryMutation: UseMutationResult<
    unknown,
    Error,
    {
      id: Id<"categories">;
      data: {
        name: string;
        color: string;
        isCategoryLockEnabled: boolean;
        matchBusinessHours: boolean;
        availabilityTimes: {
          dayOfTheWeek: string;
          startTime: string;
          endTime: string;
        }[];
      };
      menuIds: Id<"menus">[];
    }
  >;
  onUpdateAll: () => void;
  onSaveAsNew: () => void;
}

export function SharedCategoryDialog({
  updateSharedCategoryStatus,
  setUpdateSharedCategoryStatus,
  category,
  uniqueLocations,
  menusToCopyCategoryTo,
  setMenusToCopyCategoryTo,
  updateCategoryMutation,
  cloneAndUpdateCategoryMutation,
  onUpdateAll,
  onSaveAsNew,
}: SharedCategoryDialogProps) {
  if (!updateSharedCategoryStatus) {
    return null;
  }

  return (
    <Dialog onOpenChange={() => setUpdateSharedCategoryStatus(null)} open>
      <DialogContent className="gap-5" noScrollPrevention>
        {updateSharedCategoryStatus === "confirm" ? (
          <>
            <DialogHeader className="items-center space-y-0">
              <DialogTitle className="text-foreground">
                Updating a Shared Category
              </DialogTitle>
              <DialogDescription className="body text-foreground text-center">
                This category is shared with other menus. Do you want to save
                changes to this category in all shared menus?
              </DialogDescription>
            </DialogHeader>
            <div className="bg-muted space-y-1 rounded-2xl px-5 py-4">
              <div className="flex items-center justify-between gap-1">
                <p className="text-foreground">{category.name}</p>
                <div className="caption bg-muted text-muted-foreground rounded-full px-2 py-1">
                  {category.itemCount} item{category.itemCount !== 1 && "s"}
                </div>
              </div>
              <p className="body-sm text-muted-foreground">
                {category.menuRelations?.map((m) => m.menu?.name).join(", ")}
              </p>
            </div>
            <Accordion type="multiple">
              <AccordionItem
                className="border-none"
                value="affected-categories"
              >
                <AccordionTrigger className="label text-foreground hover:no-underline">
                  Affected Locations ({uniqueLocations.length})
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  {uniqueLocations.map((location) => (
                    <div
                      className="bg-muted flex items-center gap-3 rounded-2xl px-4 py-3"
                      key={location.id}
                    >
                      <img
                        alt={`${location.name} logo`}
                        className="size-6 rounded-full"
                        height={24}
                        src={
                          location.logoUrl ??
                          "/images/location-logo-placeholder.svg"
                        }
                        width={24}
                      />
                      <p className="text-foreground">{location.name}</p>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            <p className="text-foreground text-center">
              Only updating this category for one menu? Select Save Changes as
              New Category instead.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => {
                  setUpdateSharedCategoryStatus("select-menus");
                }}
                type="button"
                variant="secondary"
              >
                Save as New Category
              </Button>
              <Button
                disabled={updateCategoryMutation.isPending}
                onClick={onUpdateAll}
                type="button"
                variant="default"
              >
                Update all
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="items-center space-y-0">
              <DialogTitle className="text-foreground">
                Save Changes as New Category
              </DialogTitle>
              <DialogDescription className="body text-foreground text-center">
                Select menus to add this new category
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>Add to categories</Label>
              <MultiCombobox
                groups={["default"]}
                items={{
                  default: category.menuRelations.map(({ menu }) => ({
                    value: menu.id,
                    label: menu.name,
                  })),
                }}
                onValueChange={(d) => setMenusToCopyCategoryTo(d.default)}
                values={{
                  default: menusToCopyCategoryTo,
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => {
                  setMenusToCopyCategoryTo([]);
                  setUpdateSharedCategoryStatus("confirm");
                }}
                type="button"
                variant="secondary"
              >
                Back
              </Button>
              <Button
                disabled={cloneAndUpdateCategoryMutation.isPending}
                onClick={onSaveAsNew}
                type="button"
                variant="default"
              >
                Save New Item
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
